// Cliente de mídia da chamada (fase C4).
//
// O contrato do sidecar (`~/Developer/caller`, openapi.yaml, GET /ws/calls):
// PCM s16le mono 16 kHz binário nos dois sentidos; frame de TEXTO é controle
// (`{"type":"mute","value":true}`). O mudo é aplicado LÁ (máscara de razões
// do sidecar) — por isso o microfone continua subindo enquanto mudo, e o que
// muda é só o comando; parar de enviar aqui deixaria o pipe de uplink do
// sidecar sem alimento e o áudio picotaria ao desmutar.
//
// O ticket vem do backend (`POST /calls/:callId/media-ticket`) e é curto: por
// isso a reconexão pede um ticket NOVO a cada tentativa, em vez de repetir o
// da primeira conexão.

const TAXA = 16000;
const JITTER_SEGUNDOS = 0.12; // 120 ms de folga na descida
const TENTATIVAS = 8;
const ESPERA_MS = 700;
const BLOCO = 2048; // ~128 ms por bloco a 16 kHz — só no fallback ScriptProcessor
// 60 ms — o tamanho de frame do sidecar/meowcaller (FrameSamples=960): cada
// mensagem do WS vira exatamente um frame no `io.ReadFull` do outro lado.
const AMOSTRAS_POR_ENVIO = 960;
// Fila local do WebSocket acima disto (≈1 s de áudio) é atraso que o cliente
// vai ouvir para sempre: o send loop do meowcaller consome em ritmo fixo de
// 60 ms/frame e NUNCA drena backlog. Ao vivo, perder o bloco é melhor.
const LIMITE_DE_FILA_BYTES = TAXA * 2;

// Captura na thread de ÁUDIO (AudioWorklet), não na principal: o
// ScriptProcessor entregava o microfone pela thread do React, e qualquer
// render (a barra da chamada re-renderiza a cada segundo pelo cronômetro)
// atrasava ou perdia bloco — do outro lado virava voz picotada e com atraso
// crescente. Vai como Blob URL para não depender de asset do Vite.
const CODIGO_DO_WORKLET = `
registerProcessor("captura-pcm", class extends AudioWorkletProcessor {
	constructor() {
		super();
		this.fila = new Int16Array(${AMOSTRAS_POR_ENVIO});
		this.usado = 0;
	}
	process(entradas) {
		const canal = entradas[0] && entradas[0][0];
		if (!canal) return true;
		for (let i = 0; i < canal.length; i += 1) {
			const v = Math.max(-1, Math.min(1, canal[i]));
			this.fila[this.usado] = v < 0 ? v * 0x8000 : v * 0x7fff;
			this.usado += 1;
			if (this.usado === this.fila.length) {
				const bloco = this.fila.slice(0);
				this.port.postMessage(bloco.buffer, [bloco.buffer]);
				this.usado = 0;
			}
		}
		return true;
	}
});
`;

const paraInt16 = (float32) => {
	const saida = new Int16Array(float32.length);

	for (let i = 0; i < float32.length; i += 1) {
		// Satura em vez de dar a volta: sem o limite, um pico acima de 1.0
		// vira estouro e sai como estalo no fone do outro lado.
		const v = Math.max(-1, Math.min(1, float32[i]));
		saida[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
	}

	return saida;
};

// Abre o áudio da chamada e devolve o controle dela.
//
// `obterUrl` é assíncrono de propósito: cada tentativa de conexão pede um
// ticket novo ao backend.
export const abrirMidiaDaChamada = async ({ obterUrl, aoEstado }) => {
	const avisar = (estado, detalhe) => {
		if (aoEstado) aoEstado(estado, detalhe);
	};

	const ctx = new (window.AudioContext || window.webkitAudioContext)({
		sampleRate: TAXA,
	});

	let ws = null;
	let vivo = true;
	let mudo = false;
	let proximoInstante = 0;
	let tentativa = 0;
	let timerReconexao = null;
	// O WS pode conectar com o prompt de permissão ainda na tela: enquanto o
	// microfone não estiver na mão, o estado dito à tela é "aguardando o
	// microfone", nunca "conectado" — senão a barra mente que está tudo no ar.
	let microfonePronto = false;

	const tocar = (arrayBuffer) => {
		const amostras = new Int16Array(arrayBuffer);

		if (!amostras.length) return;

		const buffer = ctx.createBuffer(1, amostras.length, TAXA);
		const canal = buffer.getChannelData(0);

		for (let i = 0; i < amostras.length; i += 1) {
			canal[i] = amostras[i] / 0x8000;
		}

		const no = ctx.createBufferSource();
		no.buffer = buffer;
		no.connect(ctx.destination);

		// Jitter buffer: cada bloco toca depois do anterior, e nunca antes de
		// 120 ms à frente do relógio. Sem essa folga, um atraso de rede de um
		// bloco só já deixa um buraco audível.
		const agora = ctx.currentTime + JITTER_SEGUNDOS;
		proximoInstante = Math.max(agora, proximoInstante);
		no.start(proximoInstante);
		proximoInstante += buffer.duration;
	};

	const conectar = async () => {
		if (!vivo) return;

		let url = "";

		try {
			url = await obterUrl();
		} catch (err) {
			url = "";
		}

		if (!vivo) return;

		if (!url) {
			agendarReconexao();
			return;
		}

		ws = new WebSocket(url);
		ws.binaryType = "arraybuffer";

		ws.onopen = () => {
			tentativa = 0;
			proximoInstante = 0;
			// Reafirma o mudo: reconectar com o microfone aberto depois de a
			// pessoa ter silenciado é vazamento de conversa, não bug de tela.
			if (mudo) ws.send(JSON.stringify({ type: "mute", value: true }));
			avisar(microfonePronto ? "conectado" : "aguardando_microfone");
		};

		ws.onmessage = (evento) => {
			if (typeof evento.data !== "string") tocar(evento.data);
		};

		ws.onclose = () => {
			if (!vivo) return;
			avisar("reconectando", tentativa + 1);
			agendarReconexao();
		};

		ws.onerror = () => {
			if (ws) ws.close();
		};
	};

	const agendarReconexao = () => {
		if (!vivo) return;

		tentativa += 1;

		if (tentativa > TENTATIVAS) {
			avisar("perdido");
			return;
		}

		timerReconexao = setTimeout(conectar, ESPERA_MS);
	};

	// O WebSocket abre em PARALELO ao pedido de microfone, nunca depois: a
	// ESCUTA não depende do microfone, e esperar o prompt de permissão deixou
	// uma chamada real inteira no silêncio (o WS só conectou 57 s após o
	// discar; a ponte do sidecar descartou 470 de 478 frames sem consumidor).
	// O `enviar` da captura já se protege com `ws.readyState`.
	const conexaoInicial = conectar();

	avisar("aguardando_microfone");

	let stream;

	try {
		stream = await navigator.mediaDevices.getUserMedia({
			audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
		});
	} catch (err) {
		// Sem microfone não há chamada para este operador: desfaz o WS que
		// abriu em paralelo antes de repassar o erro à tela.
		vivo = false;
		clearTimeout(timerReconexao);
		if (ws) ws.close();
		ctx.close();
		throw err;
	}

	const fonte = ctx.createMediaStreamSource(stream);

	const enviar = (arrayBuffer) => {
		if (!ws || ws.readyState !== WebSocket.OPEN) return;
		// Fila local crescendo é o atraso que o outro lado nunca recupera —
		// ver LIMITE_DE_FILA_BYTES. Descarta o bloco e segue.
		if (ws.bufferedAmount > LIMITE_DE_FILA_BYTES) return;
		ws.send(arrayBuffer);
	};

	// O nó de captura (worklet ou fallback) só roda ligado a um destino. O
	// ganho zerado impede o operador de ouvir a própria voz de volta.
	const mudoDeSaida = ctx.createGain();
	mudoDeSaida.gain.value = 0;
	mudoDeSaida.connect(ctx.destination);

	let captura = null;

	if (ctx.audioWorklet) {
		const urlDoWorklet = URL.createObjectURL(
			new Blob([CODIGO_DO_WORKLET], { type: "application/javascript" })
		);

		try {
			await ctx.audioWorklet.addModule(urlDoWorklet);
			captura = new window.AudioWorkletNode(ctx, "captura-pcm", {
				numberOfInputs: 1,
				numberOfOutputs: 1,
				channelCount: 1,
			});
			captura.port.onmessage = (evento) => enviar(evento.data);
		} catch {
			// Navegador sem worklet de verdade (ou sem módulo por Blob): cai no
			// ScriptProcessor abaixo, que roda em todo lugar.
			captura = null;
		} finally {
			URL.revokeObjectURL(urlDoWorklet);
		}
	}

	if (!captura) {
		// Fallback: thread principal, blocos de 128 ms — pior sob render
		// pesado, mas funciona em qualquer navegador.
		captura = ctx.createScriptProcessor(BLOCO, 1, 1);
		captura.onaudioprocess = (evento) => {
			enviar(paraInt16(evento.inputBuffer.getChannelData(0)).buffer);
		};
	}

	fonte.connect(captura);
	captura.connect(mudoDeSaida);

	microfonePronto = true;
	if (ws && ws.readyState === WebSocket.OPEN) avisar("conectado");

	await conexaoInicial;

	return {
		silenciar(valor) {
			mudo = Boolean(valor);
			if (ws && ws.readyState === WebSocket.OPEN) {
				ws.send(JSON.stringify({ type: "mute", value: mudo }));
			}
		},
		fechar() {
			vivo = false;
			clearTimeout(timerReconexao);
			if (captura.port) captura.port.onmessage = null;
			if ("onaudioprocess" in captura) captura.onaudioprocess = null;
			try {
				captura.disconnect();
				fonte.disconnect();
				mudoDeSaida.disconnect();
			} catch (err) {
				/* já desconectado */
			}
			// Devolve o microfone: sem isto a luz da câmera/mic fica acesa e o
			// navegador mantém o dispositivo tomado depois da chamada.
			stream.getTracks().forEach((t) => t.stop());
			if (ws) ws.close();
			ctx.close();
		},
	};
};

export default abrirMidiaDaChamada;
